import React from "react";

export default function BlogWhenToSwitchJobs({ onBack, onConsultationClick }) {
  return (
    <article className="min-h-screen bg-gradient-to-b from-green-50 via-white to-indigo-50 py-20 px-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="mb-6 text-indigo-600 hover:underline font-semibold text-sm"
        >
          ← Back to Blog
        </button>

        <header className="mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            When to Switch Jobs — Signs, Strategies &amp; How to Plan Your Career Move
          </h1>
          <p className="mt-4 text-gray-500 text-sm">
            Published by <span className="font-semibold text-indigo-600">Systematic Career Planner</span> · Updated May 2026 · 8 min read
          </p>
        </header>

        <img
          src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop&q=80"
          onError={(e) => { e.target.style.display = 'none'; }}
          alt="Professional considering career switch"
          className="w-full rounded-2xl shadow-lg mb-10"
        />

        <section className="prose prose-lg max-w-none text-gray-700 space-y-8">
          <h2 className="text-2xl font-bold text-gray-800">Is It Time to Switch? The Honest Question</h2>
          <p>
            Every working professional faces this question at some point: <em>"Should I stay or should I go?"</em>
            Whether you're feeling stagnant, underpaid, or simply burned out — knowing <strong>when</strong> and
            <strong> how</strong> to switch is critical. At <strong>Systematic Career Planner</strong>, we help
            professionals navigate career transitions with the same structured approach we use for students —
            because career planning doesn't stop at graduation.
          </p>

          <h2 className="text-2xl font-bold text-gray-800">🚩 7 Clear Signs It's Time to Switch Jobs</h2>
          <div className="space-y-4">
            {[
              { num: "1", title: "You've stopped learning", desc: "If your job no longer challenges you or teaches you new skills, you're stagnating. Growth should never stop." },
              { num: "2", title: "You dread going to work", desc: "Occasional Monday blues are normal. But if you consistently feel anxious or unhappy, it's a red flag." },
              { num: "3", title: "You're underpaid for your role", desc: "Research market salaries. If you're significantly below market rate despite good performance, it's time to look around." },
              { num: "4", title: "No clear growth path", desc: "If promotions are rare, roles are flat, and your manager can't articulate your next step — the ceiling is real." },
              { num: "5", title: "Toxic work environment", desc: "Bad management, office politics, lack of recognition, or poor work-life balance takes a toll on health and happiness." },
              { num: "6", title: "Your industry is declining", desc: "If your sector is shrinking due to automation or market shifts, proactive switching is smarter than waiting for layoffs." },
              { num: "7", title: "Your values don't align", desc: "If the company's culture, ethics, or mission conflicts with your personal values, the disconnect will only grow." },
            ].map((sign) => (
              <div key={sign.num} className="flex gap-4 items-start bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <span className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 text-red-600 font-bold flex items-center justify-center">{sign.num}</span>
                <div>
                  <p className="font-semibold text-gray-800">{sign.title}</p>
                  <p className="text-sm text-gray-600">{sign.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <h2 className="text-2xl font-bold text-gray-800">⏰ When NOT to Switch</h2>
          <div className="bg-yellow-50 rounded-xl p-6 border-l-4 border-yellow-500">
            <ul className="list-disc list-inside space-y-2">
              <li><strong>During emotional highs/lows</strong> — Never quit impulsively after a bad day or a fight with your manager</li>
              <li><strong>Without a financial cushion</strong> — Have at least 3–6 months of expenses saved</li>
              <li><strong>Without a plan</strong> — Random job-hopping hurts your resume; strategic switching builds it</li>
              <li><strong>Before completing 1.5–2 years</strong> — Frequent switches before 2 years raise red flags for recruiters</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold text-gray-800">📋 The Smart Switching Strategy</h2>
          <div className="bg-indigo-50 rounded-xl p-6 border-l-4 border-indigo-500">
            <ol className="list-decimal list-inside space-y-3">
              <li>
                <strong>Self-assessment first</strong> — What do you want? Higher salary, better role, new industry,
                or better work-life balance? Be specific.
              </li>
              <li>
                <strong>Skill gap analysis</strong> — Identify what skills the new role/industry requires and start
                upskilling before you switch.
              </li>
              <li>
                <strong>Update your brand</strong> — Revamp your resume, LinkedIn profile, and portfolio. Make them
                achievement-oriented, not task-oriented.
              </li>
              <li>
                <strong>Network strategically</strong> — 70% of jobs are filled through referrals. Reach out to
                people in your target companies and roles.
              </li>
              <li>
                <strong>Negotiate smartly</strong> — Research market rates. Never accept the first offer. Factor in
                benefits, growth, and culture — not just salary.
              </li>
              <li>
                <strong>Exit gracefully</strong> — Give proper notice, complete handovers, and maintain relationships.
                Your reputation follows you.
              </li>
            </ol>
          </div>

          <h2 className="text-2xl font-bold text-gray-800">Career Switch vs Higher Studies</h2>
          <p>
            Sometimes the best "switch" isn't a new job — it's going back to education. MBA, specialized certifications,
            or even a completely new degree can open doors that job-hopping can't.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">Situation</th>
                  <th className="text-left p-3 font-semibold">Best Move</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                <tr><td className="p-3">Want higher salary in same field</td><td className="p-3">Switch jobs with negotiation</td></tr>
                <tr><td className="p-3">Want to move to management</td><td className="p-3">MBA or executive program</td></tr>
                <tr><td className="p-3">Want to change industry entirely</td><td className="p-3">Upskill + transition role</td></tr>
                <tr><td className="p-3">Burned out, need a break</td><td className="p-3">Sabbatical + career counseling</td></tr>
                <tr><td className="p-3">Want to start a business</td><td className="p-3">Save, plan, and build on the side first</td></tr>
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-800">Real-Life Consequences of Wrong Timing</h2>
          <div className="bg-red-50 rounded-xl p-6 border-l-4 border-red-400">
            <p>
              Switching too early burns bridges and creates a "job-hopper" label. Switching too late means
              years of frustration, missed opportunities, and career stagnation. The cost of indecision
              is always higher than the cost of getting expert guidance.
            </p>
          </div>

          <div className="bg-pink-50 rounded-xl p-6 mt-8 text-center">
            <p className="text-lg font-bold text-gray-800 mb-2">Planning a Career Switch?</p>
            <p className="text-gray-600 mb-4">
              Our career experts help you make the right move at the right time. Don't leave your career to chance.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {/* <a href="#contact" className="px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
                📅 Book Free Consultation
              </a> */}
              <button onClick={onConsultationClick} className="px-6 py-3 rounded-full bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition">
                📅 Book Free Consultation
              </button>
              <a
                href="https://wa.me/919920405421?text=Hi%2C%20I%20want%20career%20switch%20guidance"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 rounded-full bg-green-500 text-white font-semibold hover:bg-green-600 transition"
              >
                💬 WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </article>
  );
}
